import { makeRoute } from "@flow/common/route";
import * as Safe from "safe-portals";
import { donationMetadataSerializer, Serializers } from "@flow/common/types";
import {
  bankAccountSerializer,
  donateSettingsSerializer,
  recentDontionSerializer,
  smartDebitDDISerializer,
  smartDebitParamsSerializer,
} from "./types";
import * as perm from "@flow/common/core_permissions";

// eslint-disable-next-line woke/all
// This endpoint is required for Apple to verify our domain and enable payment using Apple Pay as part of the Donate plugin
// eslint-disable-next-line woke/all
export const getAppleVerificationFile = makeRoute(
  "/.well-known/apple-developer-merchantid-domain-association",
  "public",
  Safe.nothing,
  Safe.raw,
  "GET"
);

export const createPaypalDonationAttempt = makeRoute(
  "/api/plugin/donate/paypal/create_donation_attempt",
  "public",
  Safe.obj({
    journey_context: Serializers.journeyContext,
    currency: Safe.str,
    amount: Safe.float,
    metadata: donationMetadataSerializer,
    hide_donation: Safe.bool,
    // order_id is used for one-off donations (replaced by transaction_id when the order is captured)
    order_id: Safe.str,
    // subscription_id is used for regular (repeat) donations
    subscription_id: Safe.optional(Safe.str),
    frequency: Safe.oneOf("once", "weekly", "monthly", "yearly"),
    action_guid: Safe.uuid,
    variant_idx: Safe.int,
  }),
  Safe.obj({
    donation_attempt_guid: Safe.uuid,
  })
);

export const getStripePublicKey = makeRoute(
  "/api/plugin/donate/stripe/get_public_key",
  "public",
  Safe.nothing,
  Safe.obj({
    public_key: Safe.optional(Safe.str),
  })
);

export const createStripePaymentIntent = makeRoute(
  "/api/plugin/donate/stripe/create_payment_intent",
  "public",
  Safe.obj({
    journey_context: Serializers.journeyContext,
    action_guid: Safe.uuid,
    variant_idx: Safe.int,
    amount: Safe.float,
    metadata: donationMetadataSerializer,
    hide_donation: Safe.bool,
    payment_method_id: Safe.str,
    is_subscription: Safe.bool,
    use_prefill_member_data: Safe.bool,
  }),
  Safe.Result.serializer({
    ok: Safe.obj({
      donation_attempt_guid: Safe.str,
      intent_id: Safe.str,
      client_secret: Safe.str,
      status: Safe.str,
    }),
    error: Safe.str,
  })
);

export const recordDonationSuccess = makeRoute(
  "/api/plugin/donate/record_donation_success",
  "public",
  Safe.obj({
    context: Serializers.journeyContext,
    action_guid: Safe.uuid,
    donation_attempt_guid: Safe.uuid, // guid of donate__action record
    use_prefill_member_data: Safe.bool,
  }),
  Safe.nothing
);

export const createPaypalSubscriptionPlan = makeRoute(
  "/api/plugin/donate/paypal/create_subscription_plan",
  "public",
  Safe.obj({
    amount: Safe.float,
  }),
  Safe.obj({
    plan_id: Safe.str,
  })
);

export const validateSmartDebitDirectDebitDetails = makeRoute(
  "/api/plugin/donate/smartdebit/validate",
  "public",
  Safe.obj({
    action_guid: Safe.uuid,
    member: Safe.obj({
      name: Safe.optional(Safe.str),
      email: Safe.optional(Safe.str),
    }),
    use_prefill_member_data: Safe.bool,
    accountDetails: bankAccountSerializer,
    smartDebitParams: smartDebitParamsSerializer,
    captcha_token: Safe.optional(Safe.str),
  }),
  Safe.Result.serializer({
    ok: Safe.bool,
    error: Safe.str,
  })
);

export const createSmartDebitDirectDebit = makeRoute(
  "/api/plugin/donate/smartdebit/create",
  "public",
  Safe.obj({
    action_guid: Safe.uuid,
    variant_idx: Safe.int,
    journey_context: Serializers.journeyContext,
    use_prefill_member_data: Safe.bool,
    accountDetails: bankAccountSerializer,
    smartDebitParams: smartDebitParamsSerializer,
    metadata: donationMetadataSerializer,
    hide_donation: Safe.bool,
  }),
  Safe.Result.serializer({
    ok: Safe.obj({
      ddi: smartDebitDDISerializer,
      donate_action_guid: Safe.uuid, // guid of donate__action record
    }),
    error: Safe.str,
  })
);

export const getSettings = makeRoute(
  "/admin/api/donate/get_settings",
  [perm.VIEW_SETTINGS],
  Safe.nothing,
  donateSettingsSerializer
);

export const setSettings = makeRoute(
  "/admin/api/donate/set_settings",
  [perm.UPDATE_SETTINGS],
  donateSettingsSerializer,
  Safe.nothing
);

// Expose an endpoing to allow the frontend to fetch recent donations. Used for the donate plugin.
export const getRecentDonations = makeRoute(
  "/api/plugin/donate/donations/recent",
  "public",
  // arguments
  Safe.obj({
    journeyGuid: Safe.str,
  }),
  // response
  Safe.array(recentDontionSerializer)
);
